import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Mail, Users, CheckCircle2, XCircle, MinusCircle, ArrowLeft, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { getSnapshotDistrictContacts, emailSnapshotBulk, emailSnapshotBulkSelf } from "../../lib/api";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const CUSTOM = "__custom__";

export default function BulkDistrictSnapshotDialog({ open, onOpenChange, startDate, endDate, periodLabel }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);        // {district, student_count, contacts, selected, contactKey, email}
  const [step, setStep] = useState("configure"); // configure | review | results
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingSelf, setSendingSelf] = useState(false);
  const [selfSent, setSelfSent] = useState(null); // {recipient} once a review copy has been emailed
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep("configure");
    setNote("");
    setResults(null);
    setSelfSent(null);
    setLoading(true);
    getSnapshotDistrictContacts()
      .then((res) => {
        const data = (res.data || [])
          .filter((d) => d.student_count > 0) // only districts that have current students to send
          .map((d) => {
            const first = d.contacts?.[0];
            return {
              district: d.district,
              student_count: d.student_count,
              contacts: d.contacts || [],
              selected: true,
              contactKey: first ? first.email : CUSTOM,
              email: first ? first.email : "",
            };
          });
        setRows(data);
      })
      .catch(() => toast.error("Failed to load district contacts"))
      .finally(() => setLoading(false));
  }, [open]);

  const update = (district, patch) =>
    setRows((prev) => prev.map((r) => (r.district === district ? { ...r, ...patch } : r)));

  const onPickContact = (row, key) => {
    if (key === CUSTOM) { update(row.district, { contactKey: CUSTOM }); return; }
    const c = row.contacts.find((c) => c.email === key);
    update(row.district, { contactKey: key, email: c ? c.email : row.email });
  };

  const selectedRows = useMemo(() => rows.filter((r) => r.selected), [rows]);
  const validRows = useMemo(
    () => selectedRows.filter((r) => EMAIL_RE.test((r.email || "").trim())),
    [selectedRows]
  );
  const invalidCount = selectedRows.length - validRows.length;

  const setAll = (val) => setRows((prev) => prev.map((r) => ({ ...r, selected: val })));

  const goReview = () => {
    if (selectedRows.length === 0) { toast.error("Select at least one district"); return; }
    if (invalidCount > 0) { toast.error(`${invalidCount} selected district(s) have a missing or invalid email`); return; }
    setStep("review");
  };

  const buildPayload = () => {
    const payload = {
      sends: validRows.map((r) => ({ district: r.district, recipient_email: r.email.trim() })),
      note: note.trim(),
      period_label: periodLabel || "",
    };
    if (startDate && endDate) { payload.start_date = startDate; payload.end_date = endDate; }
    return payload;
  };

  const sendSelf = async () => {
    setSendingSelf(true);
    try {
      const res = await emailSnapshotBulkSelf(buildPayload());
      const { recipient, count = 0, suppressed } = res.data || {};
      setSelfSent({ recipient });
      if (suppressed) toast.success(`${count} report(s) queued to ${recipient} — email sending is off in preview; it will arrive on the live site.`);
      else toast.success(`Review copy of ${count} report(s) sent to ${recipient}. Check your inbox before sending to districts.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the review copy");
    } finally { setSendingSelf(false); }
  };

  const sendAll = async () => {
    setSending(true);
    try {
      const res = await emailSnapshotBulk(buildPayload());
      setResults(res.data);
      setStep("results");
      const { sent = 0, failed = 0, suppressed } = res.data || {};
      if (suppressed) toast.success(`${sent} report(s) queued — email sending is off in preview; they will send on the live site.`);
      else if (failed > 0) toast.warning(`${sent} sent, ${failed} failed`);
      else toast.success(`Snapshot sent to ${sent} district contact(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk send failed");
    } finally { setSending(false); }
  };

  const StatusIcon = ({ status }) => {
    if (status === "sent" || status === "suppressed") return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    if (status === "skipped") return <MinusCircle className="h-4 w-4 text-slate-400" />;
    return <XCircle className="h-4 w-4 text-red-600" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulk-snapshot-dialog">
        <DialogHeader>
          <DialogTitle>Send Weekly Snapshot to all Districts</DialogTitle>
          <DialogDescription>
            Each district contact receives a PDF with <b>only their district's current students</b> — never the whole school, and never former students or referrals.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="py-10 text-center text-muted-foreground">Loading district contacts…</p>
        ) : step === "configure" ? (
          <>
            {rows.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">No districts with current students were found.</p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{selectedRows.length} of {rows.length} districts selected</span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setAll(true)} data-testid="bulk-select-all">Select all</Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setAll(false)} data-testid="bulk-clear-all">Clear</Button>
                  </div>
                </div>
                <div className="max-h-[52vh] overflow-y-auto space-y-2 pr-1">
                  {rows.map((r) => (
                    <div key={r.district} className={`rounded-lg border p-3 ${r.selected ? "border-[#00305E]/40 bg-[#00305E]/[0.03]" : "border-slate-200"}`} data-testid={`bulk-row-${r.district}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox checked={r.selected} onCheckedChange={(v) => update(r.district, { selected: !!v })} data-testid={`bulk-check-${r.district}`} className="mt-1" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-sm text-[#00305E] truncate">{r.district}</span>
                            <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 shrink-0"><Users className="h-3 w-3" />{r.student_count} student{r.student_count === 1 ? "" : "s"}</span>
                          </div>
                          {r.selected && (
                            <div className="mt-2 grid gap-2 sm:grid-cols-2">
                              <div>
                                <Label className="text-[11px] text-slate-500">Contact</Label>
                                <Select value={r.contactKey} onValueChange={(v) => onPickContact(r, v)}>
                                  <SelectTrigger className="h-8 text-xs" data-testid={`bulk-contact-${r.district}`}><SelectValue placeholder="Choose contact" /></SelectTrigger>
                                  <SelectContent>
                                    {r.contacts.map((c) => (
                                      <SelectItem key={c.email} value={c.email}>{c.name} ({c.label})</SelectItem>
                                    ))}
                                    <SelectItem value={CUSTOM}>Custom email…</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-[11px] text-slate-500">Recipient email</Label>
                                <Input
                                  type="email"
                                  value={r.email}
                                  onChange={(e) => update(r.district, { email: e.target.value, contactKey: CUSTOM })}
                                  placeholder="district.contact@example.com"
                                  className={`h-8 text-xs ${r.email && !EMAIL_RE.test(r.email.trim()) ? "border-red-400" : ""}`}
                                  data-testid={`bulk-email-${r.district}`}
                                />
                              </div>
                              {r.contacts.length === 0 && (
                                <p className="sm:col-span-2 text-[11px] text-amber-600">No saved contact for this district — enter an email above.</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {invalidCount > 0 && (
                  <p className="text-xs text-red-600">{invalidCount} selected district(s) still need a valid email.</p>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={goReview} disabled={selectedRows.length === 0 || invalidCount > 0} data-testid="bulk-review-btn">
                Review ({validRows.length})
              </Button>
            </DialogFooter>
          </>
        ) : step === "review" ? (
          <>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Confirm each district and recipient below. Clicking Send All emails every row at once.</p>
              <div className="max-h-[38vh] overflow-y-auto rounded-lg border divide-y" data-testid="bulk-review-list">
                {validRows.map((r) => (
                  <div key={r.district} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`bulk-review-${r.district}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-[#00305E] truncate">{r.district}</div>
                      <div className="text-[11px] text-slate-500">{r.student_count} student{r.student_count === 1 ? "" : "s"}</div>
                    </div>
                    <div className="text-xs text-slate-700 truncate text-right">{r.email.trim()}</div>
                  </div>
                ))}
              </div>
              <div>
                <Label htmlFor="bulk-note" className="text-sm">Note to all recipients (optional)</Label>
                <Textarea id="bulk-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a short message included in every email…" data-testid="bulk-note" />
              </div>
              <div className="rounded-lg border border-[#00305E]/30 bg-[#00305E]/[0.03] p-3">
                <div className="flex items-start gap-2">
                  <Eye className="h-4 w-4 text-[#00305E] mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-600">Want to check the data first? Email yourself one copy of <b>every</b> district PDF — exactly what each district will receive — before anything goes out.</p>
                    {selfSent && (
                      <p className="mt-1 text-[11px] text-green-700 flex items-center gap-1" data-testid="bulk-self-sent-note">
                        <CheckCircle2 className="h-3.5 w-3.5" />Review copy sent to {selfSent.recipient}.
                      </p>
                    )}
                  </div>
                  <Button variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={sendSelf} disabled={sendingSelf || sending || validRows.length === 0} data-testid="bulk-send-self-btn">
                    <Eye className="h-3.5 w-3.5 mr-1.5" />{sendingSelf ? "Sending…" : "Email a copy to me"}
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter className="sm:justify-between">
              <Button variant="outline" onClick={() => setStep("configure")} disabled={sending || sendingSelf}><ArrowLeft className="h-4 w-4 mr-1.5" />Back</Button>
              <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={sendAll} disabled={sending || sendingSelf || validRows.length === 0} data-testid="bulk-send-all-btn">
                <Mail className="h-4 w-4 mr-1.5" />{sending ? "Sending…" : `Send All (${validRows.length})`}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-2 max-h-[52vh] overflow-y-auto" data-testid="bulk-results-list">
              {(results?.results || []).map((r) => (
                <div key={r.district} className="flex items-center justify-between gap-3 rounded border px-3 py-2 text-sm" data-testid={`bulk-result-${r.district}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <StatusIcon status={r.status} />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.district}</div>
                      <div className="text-[11px] text-slate-500 truncate">{r.recipient}</div>
                    </div>
                  </div>
                  <span className={`text-xs font-medium shrink-0 ${r.status === "failed" ? "text-red-600" : r.status === "skipped" ? "text-slate-400" : "text-green-600"}`}>
                    {r.status === "suppressed" ? "queued" : r.status}{r.message && r.status === "failed" ? ` — ${r.message}` : ""}
                  </span>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={() => onOpenChange(false)} data-testid="bulk-done-btn">Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
